// E13 Earned autonomy and E14 spending caps on the screens: the Settings sections (levels, kill switch, spend against
// caps), the Approvals cards (a promotion proposal, a budget or demotion notice) and the "Auto-approved" list.
// Every change goes through the act function (decide_autonomy, demote_autonomy, set_autonomy_kill); the database
// decides whether a kind still qualifies.
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { Crm } from "../lib/crm";
import { useCrm } from "../lib/session";
import { useAsync } from "../lib/useAsync";
import type { AutonomyKind, AutonomyLevel, BudgetStatus, HumanTask } from "../lib/types";
import { fmtDateTime, humanise, plural } from "../lib/format";
import { Card } from "../components/kit";
import { FIND_PICK_KIND, kindsForModule, type ApprovalModule } from "../lib/approvalsFind";
import { decideScope } from "../lib/approvalsConnect";
import { CampaignSettingsNote } from "./pipeline/ApprovalsGrid";
import { Async, Notice, ReasonDialog, StatusChip } from "../components/ui";

export const LEVELS: Record<AutonomyLevel, { label: string; hint: string }> = {
  approve_all: { label: "You approve each one", hint: "Every message of this kind waits for you." },
  sample: { label: "You approve a sample", hint: "About 1 in 10 waits for you; the rest go out once checked by the rules." },
  auto: { label: "Automatic", hint: "Approved by the system; the weekly sample check reads about 1 in 20." },
};

export function levelLabel(level: string | null | undefined): string {
  return LEVELS[(level ?? "approve_all") as AutonomyLevel]?.label ?? humanise(String(level));
}

const VENDOR_LABELS: Record<string, string> = {
  model: "Claude (AI)",
  scribeless: "Scribeless letters",
  millionverifier: "MillionVerifier checks",
  hunter: "Hunter searches",
  apify: "Apify runs",
  google_places: "Google Places searches",
};

export const usd = (n: number | null | undefined) => `$${Number(n ?? 0).toFixed(2)}`;

function cameBack(k: AutonomyKind): string {
  const parts = [
    k.complaints ? `${k.complaints} ${k.complaints === 1 ? "complaint" : "complaints"}` : "",
    k.unsubscribes ? `${k.unsubscribes} unsubscribed` : "",
    k.bounces ? `${k.bounces} bounced` : "",
    k.negative_replies ? `${k.negative_replies} said no` : "",
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "Nothing";
}

/** What the row says about moving up. */
export function statusText(k: AutonomyKind): string {
  if (k.kind === FIND_PICK_KIND) return k.level === "auto" ? "Switched on by you in Settings › Approvals and spend › Outreach approvals." : "Off: switch it on in Settings › Approvals and spend › Outreach approvals.";
  if (k.never_auto) return `Always needs you: ${k.never_auto}.`;
  if (k.level === "auto") return "At the top level.";
  if (k.proposal_task) return `Ready: you can move it to “${levelLabel(k.next_level)}”.`;
  if (k.eligible) return "Ready; the ops agent will propose it this week.";
  return `Not yet: ${k.blockers.join("; ")}.`;
}

/** Move up a level (when a proposal is waiting) and back to approving each one: the buttons for one kind. */
export function KindActions({ k, crm, onChanged }: { k: AutonomyKind; crm: Crm; onChanged: (msg: string, tone?: "ok" | "bad") => void }) {
  const [busy, setBusy] = useState(false);
  const [demoting, setDemoting] = useState(false);
  const promote = async () => {
    if (!k.proposal_task) return;
    setBusy(true);
    try {
      const r = await crm.decideAutonomy(k.proposal_task, true);
      if (r.status === "promoted") onChanged(`${k.label}: now “${levelLabel(r.level)}”.`);
      else onChanged(`${k.label} no longer qualifies, so nothing changed${r.blockers?.length ? `: ${r.blockers.join("; ")}` : ""}.`, "bad");
    } catch (e) {
      onChanged((e as Error).message, "bad");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <span className="inline">
        {k.proposal_task && !k.never_auto && (
          <button type="button" className="btn btn-small" disabled={busy} onClick={() => void promote()}>
            Move to “{levelLabel(k.next_level)}”
          </button>
        )}
        {k.level !== "approve_all" && (
          <button type="button" className="btn btn-small" disabled={busy} onClick={() => setDemoting(true)}>
            Back to approving each one
          </button>
        )}
      </span>
      {demoting && (
        <ReasonDialog
          title={`Approve every ${k.label.toLowerCase()} again`}
          label="Why? (optional)"
          confirm="Approve each one again"
          optional
          onClose={() => setDemoting(false)}
          onConfirm={async (reason) => {
            await crm.demoteAutonomy(k.kind, reason || undefined);
            onChanged(`${k.label}: back to approving each one.`);
          }}
        />
      )}
    </>
  );
}

function KindRow({ k, crm, onChanged }: { k: AutonomyKind; crm: Crm; onChanged: (msg: string, tone?: "ok" | "bad") => void }) {
  return (
    <tr className={k.never_auto ? "muted" : undefined}>
      <td data-label="Kind">
        <strong>{k.label}</strong>
      </td>
      <td data-label="Level">
        <span title={LEVELS[k.level]?.hint}>{levelLabel(k.level)}</span>
        {k.changed_at && <div className="muted small">since {fmtDateTime(k.changed_at)}</div>}
      </td>
      <td data-label="You decided">
        {k.decided} ({k.approved_unedited} as written, {k.approved_edited} edited, {k.rejected} rejected)
        {k.decided > 0 && <div className="muted small">{k.edited_pct}% edited</div>}
      </td>
      <td data-label="Approved by the system">{k.auto_approved}</td>
      <td data-label="Came back">{cameBack(k)}</td>
      <td data-label="Status" className="small">
        {statusText(k)}
      </td>
      <td data-label="">
        <KindActions k={k} crm={crm} onChanged={onChanged} />
      </td>
    </tr>
  );
}

/** A settings tile as its own Card, or bare (no heading or card) inside a settings card that already has them. A bare
 *  tile tells that card its state line through onLine, so the card's subtitle says it without loading anything twice. */
export function SettingsTile({ bare, id, title, subtitle, defaultOpen, onLine, children }: { bare: boolean; id: string; title: string; subtitle: string; defaultOpen?: boolean; onLine?: (line: string) => void; children: ReactNode }) {
  useEffect(() => {
    onLine?.(subtitle);
  }, [subtitle, onLine]);
  if (bare) return <div className="settings-tile">{children}</div>;
  return (
    <Card id={id} level={2} title={title} subtitle={subtitle} defaultOpen={defaultOpen}>
      {children}
    </Card>
  );
}

/** The plain name for kinds the database has no label for yet. */
export function withLabels(rows: AutonomyKind[]): AutonomyKind[] {
  return rows.map((r) => (r.kind === FIND_PICK_KIND ? { ...r, label: "Find relevant companies" } : r));
}

/** One line for a module's card: how many of its kinds run by themselves. */
export function moduleLine(rows: AutonomyKind[] | null | undefined): string {
  if (!rows) return "Loading what runs by itself";
  if (!rows.length) return "Nothing recorded yet";
  if (rows.some((r) => r.kill)) return "Kill switch on: everything waits for you";
  const auto = rows.filter((r) => r.level === "auto").length;
  const sample = rows.filter((r) => r.level === "sample").length;
  if (!auto && !sample) return `You approve each one · ${rows.length} ${plural(rows.length, "kind")}`;
  return `${auto} of ${rows.length} automatic${sample ? `, ${sample} sampled` : ""}`;
}

/** One line for the autonomy card: the kill switch, and how many kinds run by themselves. */
export function autonomyLine(rows: AutonomyKind[] | null | undefined): string {
  if (!rows) return "Loading what runs by itself";
  if (rows.some((r) => r.kill)) return "Kill switch on: every message waits for you";
  const auto = rows.filter((r) => r.level === "auto").length;
  const sample = rows.filter((r) => r.level === "sample").length;
  return `Kill switch off · ${auto} of ${rows.length} kinds automatic${sample ? `, ${sample} sampled` : ""}`;
}

/** Settings → Autonomy: every kind with its level and record, the promote and demote controls, and the kill switch.
 *  `bare` leaves out the card, for a settings card that already has one (Settings › Approvals and spend). */
export function AutonomySettings({ bare = false, defaultOpen = true, onLine, module }: { bare?: boolean; defaultOpen?: boolean; onLine?: (line: string) => void; module?: ApprovalModule } = {}) {
  const crm = useCrm();
  const [version, setVersion] = useState(0);
  // With a module, only that module's kinds (its own Rules › Approvals page); the kill switch stays business-wide.
  const stats = useAsync(async () => kindsForModule(withLabels(await crm.autonomyStats()), module), [crm, version, module]);
  const [notice, setNotice] = useState<{ text: string; tone: "ok" | "bad" } | null>(null);
  const changed = (text: string, tone: "ok" | "bad" = "ok") => {
    setNotice({ text, tone });
    setVersion((v) => v + 1);
  };
  return (
    <SettingsTile bare={bare} id="autonomy-section" title={module ? "What runs by itself" : "Autonomy"} subtitle={module ? moduleLine(stats.data) : autonomyLine(stats.data)} defaultOpen={defaultOpen} onLine={onLine}>
      {module ? (
        <p className="muted">
          Each kind earns its way from “you approve each one” to “you approve a sample” to “automatic”, one step at a time. Nothing moves up without you. The switch
          that stops everything is in <Link to="/settings/approvals">Settings › Approvals and spend</Link>.
        </p>
      ) : (
      <p className="muted">
        Each kind of message earns its way from “you approve each one” to “you approve a sample” to “automatic”, one step at a time. The ops agent proposes a step in{" "}
        <Link to="/ask">Ask Ogma</Link> once you have approved enough with few edits; nothing moves up without you. A spam complaint, a bounce spike, a complaint
        reply or a heavily edited sample sends it straight back. First messages to someone new, campaigns, deal messages and anything about price, valuation or legal
        terms always wait for you.
      </p>
      )}
      <Notice text={notice?.text ?? null} tone={notice?.tone} />
      {/* Connect package 4: moving a kind back here leaves the campaigns' own settings; say which still send */}
      {(!module || module === "connect") && <CampaignSettingsNote key={version} />}
      <Async state={stats} label="Loading autonomy">
        {(rows) => {
          const kill = rows.some((r) => r.kill);
          if (module && !rows.length) return <p className="muted">Nothing here has a record yet. Each kind appears once Ogma has drafted one for you.</p>;
          return (
            <>
              {module ? (
                kill && (
                  <p className="notice notice-bad" role="status">
                    <strong>The kill switch is on: every message waits for you.</strong>
                  </p>
                )
              ) : (
                <KillSwitch kill={kill} onChanged={changed} />
              )}
              <div className="table-wrap">
                <table className="stack autonomy">
                  <thead>
                    <tr>
                      <th scope="col">Kind of message</th>
                      <th scope="col">Level</th>
                      <th scope="col">You decided since then</th>
                      <th scope="col">Approved by the system</th>
                      <th scope="col">Came back</th>
                      <th scope="col">Status</th>
                      <th scope="col">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((k) => (
                      <KindRow key={k.kind} k={k} crm={crm} onChanged={changed} />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          );
        }}
      </Async>
    </SettingsTile>
  );
}

/**
 * E13 The kill switch: whether every message waits for the owner, and the button that stops all automatic approval or
 * lets kinds earn it again (set_autonomy_kill). Shown in Settings › Approvals and spend and in Settings ›
 * Danger zone (#471).
 */
export function KillSwitch({ kill, onChanged }: { kill: boolean; onChanged: (text: string) => void }) {
  const crm = useCrm();
  const [killing, setKilling] = useState<null | boolean>(null);
  return (
    <>
      <div className={`notice ${kill ? "notice-bad" : ""}`} role="status">
        <p>
          {kill ? <strong>The kill switch is on: every message waits for you.</strong> : <>Kill switch is off: kinds that have earned it are approved by the system.</>}
        </p>
        <button type="button" className={`btn btn-small${kill ? "" : " btn-danger"}`} onClick={() => setKilling(!kill)}>
          {kill ? "Let kinds earn approval again" : "Stop all automatic approval"}
        </button>
      </div>
      {killing !== null && (
        <ReasonDialog
          title={killing ? "Stop all automatic approval?" : "Let kinds earn approval again?"}
          label={killing ? "Why? (optional) Every kind goes back to “you approve each one”." : "Why? (optional) Each kind starts again at “you approve each one”."}
          confirm={killing ? "Stop it" : "Switch it off"}
          optional
          onClose={() => setKilling(null)}
          onConfirm={async (reason) => {
            const on = killing;
            await crm.setAutonomyKill(on, reason || undefined);
            onChanged(on ? "Kill switch on: every message waits for you." : "Kill switch off. Kinds can earn approval again.");
          }}
        />
      )}
    </>
  );
}

/** Settings: spend today and this month against the caps (E14), under the E12 spend tile. */
export function BudgetTile({ bare = false, onLine }: { bare?: boolean; onLine?: (line: string) => void } = {}) {
  const crm = useCrm();
  const status = useAsync(() => crm.budgetStatus(), [crm]);
  const over = (status.data ?? []).filter((b) => b.over);
  const line = !status.data
    ? "Loading spend against caps"
    : over.length
      ? `Cap reached: ${over.map((b) => VENDOR_LABELS[b.kind] ?? humanise(b.kind)).join(", ")}`
      : `All ${status.data.length} within their caps`;
  return (
    <SettingsTile bare={bare} id="budget-section" title="Spending caps" subtitle={line} onLine={onLine}>
      <p className="muted">
        Paid calls stop when a cap is reached, and you get a “Budget reached” task. To change a cap, ask the assistant for a playbook change (<code>budget</code> in the
        settings).
      </p>
      <Async state={status} label="Loading spend against caps">
        {(rows) => (
          <div className="table-wrap">
            <table className="stack budget">
              <thead>
                <tr>
                  <th scope="col">What</th>
                  <th scope="col">Today</th>
                  <th scope="col">This month</th>
                  <th scope="col">Estimated cost a call</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b: BudgetStatus) => (
                  <tr key={b.kind}>
                    <td data-label="What">
                      {VENDOR_LABELS[b.kind] ?? humanise(b.kind)} {b.over && <span className="chip chip-bad">Cap reached</span>}
                    </td>
                    <td data-label="Today">
                      {usd(b.spent_day)} of {usd(b.cap_day)}
                    </td>
                    <td data-label="This month">
                      {usd(b.spent_month)} of {usd(b.cap_month)}
                    </td>
                    <td data-label="Estimated cost a call">{b.kind === "model" ? "By tokens" : b.cost_per_call_usd ? `$${Number(b.cost_per_call_usd).toFixed(3)}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Async>
    </SettingsTile>
  );
}

// ---------------------------------------------------------------- Approvals
interface CardProps {
  crm: Crm;
  task: HumanTask;
  onDone: () => void;
}

/** "Promote follow-up emails to sampling?": the record behind it, then accept or decline. */
export function PromotionTask({ crm, task, onDone }: CardProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [declining, setDeclining] = useState(false);
  const p = task.payload;
  const s = (p.stats ?? {}) as Record<string, number>;
  const decide = async (accept: boolean, note?: string) => {
    setBusy(true);
    setError(null);
    try {
      // Connect package 4: a proposal for one campaign's step (autonomy_scope_promotion) is decided the same way
      const r = task.kind === "autonomy_scope_promotion" ? await decideScope(crm, task.id, accept, note) : await crm.decideAutonomy(task.id, accept, note);
      if (accept && r.status !== "promoted") {
        setError(`It no longer qualifies, so nothing changed${r.blockers?.length ? `: ${r.blockers.join("; ")}` : ""}.`);
        setBusy(false);
        return;
      }
      onDone();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };
  return (
    <div>
      <p className="card-lede">
        <strong>{String(p.title ?? "Promote this kind of message?")}</strong>
      </p>
      <p>
        From “{levelLabel(String(p.from ?? "approve_all"))}” to “{levelLabel(String(p.to ?? "sample"))}”. {LEVELS[String(p.to) as AutonomyLevel]?.hint ?? ""}
      </p>
      <ul className="small">
        <li>
          {s.decided ?? 0} decided by you: {s.approved_unedited ?? 0} as written, {s.approved_edited ?? 0} edited ({s.edited_pct ?? 0}%), {s.rejected ?? 0} rejected
        </li>
        <li>
          Came back: {s.complaints ?? 0} complaints, {s.unsubscribes ?? 0} unsubscribed, {s.bounces ?? 0} bounced, {s.negative_replies ?? 0} said no
        </li>
      </ul>
      <p className="muted small">It goes back to “you approve each one” at once after a spam complaint, a bounce spike, a complaint reply or a heavily edited sample. You can also move it back in Settings.</p>
      <Notice text={error} tone="bad" />
      <div className="action-bar">
        <button type="button" className="btn" disabled={busy} onClick={() => setDeclining(true)}>
          Not yet
        </button>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void decide(true)}>
          {busy ? "Saving…" : `Yes, move to “${levelLabel(String(p.to ?? "sample"))}”`}
        </button>
      </div>
      {declining && (
        <ReasonDialog title="Keep approving each one" label="Why? (optional)" confirm="Not yet" optional onClose={() => setDeclining(false)} onConfirm={(reason) => decide(false, reason || undefined)} />
      )}
    </div>
  );
}

/** "Budget reached: raise or wait" and "Approvals back on": what happened and what to do, then mark as seen. */
export function EngineNoticeTask({ crm, task, onDone }: CardProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const p = task.payload;
  const budget = task.kind === "budget_reached";
  const what = budget
    ? `${VENDOR_LABELS[String(p.kind)] ?? humanise(String(p.kind ?? ""))}: ${p.period === "day" ? "today's" : "this month's"} cap of ${usd(Number(p.cap_usd))} is reached (${usd(Number(p.spent_usd))} spent).`
    : `${((p.labels ?? []) as string[]).join(", ") || "Some messages"} wait for you again: ${String(p.reason ?? "")}.`;
  return (
    <div>
      <p className="card-lede">
        <strong>{what}</strong>
      </p>
      {typeof p.what_to_do === "string" && <p>{p.what_to_do}</p>}
      {budget && (
        <p className="muted small">
          See <Link to="/settings/approvals#budget-section">spending caps in Settings › Approvals and spend</Link>.
        </p>
      )}
      <Notice text={error} tone="bad" />
      <div className="action-bar">
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await crm.completeTask(task.id, "done", { seen: true });
              onDone();
            } catch (e) {
              setError((e as Error).message);
              setBusy(false);
            }
          }}
        >
          {busy ? "Saving…" : "Mark as seen"}
        </button>
      </div>
    </div>
  );
}

/** Approvals → Auto-approved: what the system approved by itself in the last 14 days, to read after the fact. */
export function AutoApprovedList() {
  const crm = useCrm();
  const list = useAsync(() => crm.autoApproved(), [crm]);
  if (list.error || !list.data || list.data.length === 0) return null;   // nothing approved by the system: no section
  const n = list.data.length;
  // One card for the list (closed: nothing here waits for you), one row card per message to read.
  return (
    <Card id="auto-approved" level={2} title="Auto-approved" subtitle={`${n} ${plural(n, "message")} in the last 14 days, approved by the system`} chip={<span className="pill-count">{n}</span>}>
      <p className="muted small">Kinds of message that have earned it were approved by the system. Read any of them; to approve each one again, use Settings → Autonomy.</p>
      <div className="auto-approved">
        {list.data.map((a) => (
          <Card
            key={a.id}
            id={`auto-${a.id}`}
            persist={false}
            variant="row"
            level={3}
            title={`${a.label}: ${[a.person_name, a.organisation_name].filter(Boolean).join(", ") || humanise(a.channel)}${a.subject ? ` — ${a.subject}` : ""}`}
            subtitle={`${humanise(a.channel)}, approved by the system`}
            chip={<StatusChip status={a.status} />}
            meta={fmtDateTime(a.sent_at ?? a.approved_at)}
          >
            <blockquote className="message-body">{a.body}</blockquote>
            {a.organisation_id && (
              <p className="small">
                <Link to={`/contacts/companies/${a.organisation_id}`}>Open the firm</Link>
              </p>
            )}
          </Card>
        ))}
      </div>
    </Card>
  );
}
